import { useQueries, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { createOrderV2Client } from '../api/client';
import { orderV2QueryKeys } from '../api/queryKeys';
import { SavedSnippet, SavedSnippetFieldKind } from '../api/types';

const useSavedSnippets = (billingAccountId: number | null) => {
  const enabled = billingAccountId != null;
  const queryClient = useQueryClient();

  const queries = useQueries({
    queries: [
      {
        queryKey: enabled
          ? orderV2QueryKeys.savedSnippets(
              billingAccountId,
              'address_instructions',
            )
          : ['orders_v2', 'saved_snippets', 'disabled', 'address_instructions'],
        queryFn: () =>
          createOrderV2Client
            .listSavedSnippets(
              billingAccountId as number,
              'address_instructions',
            )
            .then((result) => result || []),
        enabled,
      },
      {
        queryKey: enabled
          ? orderV2QueryKeys.savedSnippets(
              billingAccountId,
              'check_in_instructions',
            )
          : [
              'orders_v2',
              'saved_snippets',
              'disabled',
              'check_in_instructions',
            ],
        queryFn: () =>
          createOrderV2Client
            .listSavedSnippets(
              billingAccountId as number,
              'check_in_instructions',
            )
            .then((result) => result || []),
        enabled,
      },
    ],
  });

  const addressSnippets = (queries[0].data ?? []) as SavedSnippet[];
  const checkInSnippets = (queries[1].data ?? []) as SavedSnippet[];

  // Selective refresh — callers invoke this after saving a snippet so
  // only the matching slot re-fetches. Mirrors the legacy helper's
  // narrow scope.
  const refreshSnippetsForKind = useCallback(
    async (fieldKind: SavedSnippetFieldKind) => {
      if (!enabled) return;
      await queryClient.invalidateQueries({
        queryKey: orderV2QueryKeys.savedSnippets(billingAccountId, fieldKind),
      });
    },
    [enabled, billingAccountId, queryClient],
  );

  return {
    addressSnippets,
    checkInSnippets,
    refreshSnippetsForKind,
  };
};

export default useSavedSnippets;
